import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ShieldCheck } from 'lucide-react';

import { EmptyState } from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/lib/auth';
import { listAgeChecks, listLimitChecks, listSyncQueue } from '@/lib/compliance';

/**
 * The compliance record.
 *
 * Everything here is written by the system as it enforces the rules: an age
 * check at the door or at checkout, a purchase limit tested against what
 * somebody already bought today, a job waiting to reach the state's
 * track-and-trace. None of it is editable, which is the point -- an audit
 * trail that can be corrected proves nothing.
 */
export const Route = createFileRoute('/compliance/')({
  component: CompliancePage,
});

function CompliancePage() {
  const { activeAccountId } = useAuth();
  const accountId = activeAccountId ?? '';

  const ageChecks = useQuery({
    queryKey: ['age-checks', accountId],
    queryFn: () => listAgeChecks(accountId),
    enabled: Boolean(accountId),
  });

  const limits = useQuery({
    queryKey: ['limit-checks', accountId],
    queryFn: () => listLimitChecks(accountId),
    enabled: Boolean(accountId),
  });

  const queue = useQuery({
    queryKey: ['metrc-queue', accountId],
    queryFn: () => listSyncQueue(accountId),
    enabled: Boolean(accountId),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Compliance</h1>
        <p className="text-muted-foreground text-sm">
          What the system checked, and what it is still telling the state.
        </p>
      </div>

      <Tabs defaultValue="limits">
        <TabsList>
          <TabsTrigger value="limits">Purchase limits</TabsTrigger>
          <TabsTrigger value="age">Age checks</TabsTrigger>
          <TabsTrigger value="metrc">Metrc queue</TabsTrigger>
        </TabsList>

        <TabsContent value="limits" className="mt-4">
          {limits.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : limits.data?.length ? (
            <ul className="divide-y rounded-lg border text-sm">
              {limits.data.map((check) => (
                <li key={check.id} className="flex flex-wrap items-start gap-3 px-4 py-2.5">
                  <Badge variant={check.passed ? 'secondary' : 'destructive'}>
                    {check.passed ? 'Allowed' : 'Refused'}
                  </Badge>

                  <div className="min-w-0 flex-1">
                    {check.lines.length ? (
                      check.lines.map((line) => (
                        <p key={line.category} className="truncate">
                          <span className="capitalize">{line.category}</span>{' '}
                          <span className="text-muted-foreground">
                            {line.attempted} asked for
                            {line.consumed > 0 ? `, ${line.consumed} already taken` : ''}
                            {line.limit == null
                              ? ''
                              : ` · limit ${line.limit} ${line.unit ?? ''} in ${line.windowHours ?? 24}h`}
                          </span>
                        </p>
                      ))
                    ) : (
                      <p className="text-muted-foreground">Nothing limited in this basket.</p>
                    )}
                  </div>

                  <span className="text-muted-foreground shrink-0 text-xs">
                    {formatWhen(check.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState>
              <ShieldCheck className="mx-auto mb-2 size-6" />
              No limit checks recorded yet.
            </EmptyState>
          )}
        </TabsContent>

        <TabsContent value="age" className="mt-4">
          {ageChecks.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : ageChecks.data?.length ? (
            <ul className="divide-y rounded-lg border text-sm">
              {ageChecks.data.map((check) => (
                <li key={check.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                  <Badge variant={check.passed ? 'secondary' : 'destructive'}>
                    {check.passed ? 'Passed' : 'Refused'}
                  </Badge>
                  <span className="min-w-0 flex-1 truncate">
                    {check.context}
                    {check.method ? ` · ${check.method}` : ''}
                  </span>
                  <span className="text-muted-foreground shrink-0 text-xs">
                    {formatWhen(check.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState>
              Nothing recorded. The storefront's gate and the register both write here.
            </EmptyState>
          )}
        </TabsContent>

        <TabsContent value="metrc" className="mt-4">
          {queue.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : queue.data?.length ? (
            <ul className="divide-y rounded-lg border text-sm">
              {queue.data.map((job) => (
                <li key={job.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                  <Badge variant={job.status === 'failed' ? 'destructive' : 'secondary'}>
                    {job.status}
                  </Badge>
                  <span className="min-w-0 flex-1 truncate">
                    {job.operation} {job.entityType}
                    {job.lastError ? ` · ${job.lastError}` : ''}
                  </span>
                  <span className="text-muted-foreground shrink-0 text-xs">
                    {job.attempts}/{job.maxAttempts} · {formatWhen(job.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState>
              The queue is empty. Jobs appear here when Metrc reporting is connected and something
              needs sending.
            </EmptyState>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function formatWhen(value: string): string {
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value),
  );
}
